import * as React from "react";
import isPropValid from "@emotion/is-prop-valid";
import { useTheme } from "styled-components";
import type { TypeChartLegendProps } from "./ChartLegendTypes";
import { getChartLegendSwatchColor } from "./chartLegendTheme";

type ChartLegendStyle = React.CSSProperties & Record<string, string | number>;

const classNames = (...values: (string | undefined | false)[]) =>
  values.filter(Boolean).join(" ");

const ChartLegendTailwind = ({
  legendLabels,
  stacked,
  theme: paletteTheme = "datavizRotation",
  qa,
  as,
  className,
  style,
  forwardedAs: _forwardedAs,
  ...rest
}: TypeChartLegendProps) => {
  const racineTheme = useTheme();
  const Component = as || "div";
  const inline = !stacked;
  const classes = classNames(
    "seeds-chart-legend",
    inline && "seeds-chart-legend-inline",
    className
  );
  const componentStyle: ChartLegendStyle = {
    "--chart-legend-label-color": racineTheme.colors.text.subtext,
    "--chart-legend-swatch-gap": racineTheme.space[300],
    "--chart-legend-swatch-size": racineTheme.space[350],
    "--chart-legend-label-gap": racineTheme.space[450],
    "--chart-legend-font-family": racineTheme.fontFamily,
    "--chart-legend-font-size": racineTheme.typography[200].fontSize!,
    "--chart-legend-line-height": racineTheme.typography[200].lineHeight!,
    ...style,
  };
  const callerProps = rest as Record<string, unknown>;
  const rootProps =
    typeof Component === "string"
      ? Object.fromEntries(
          Object.entries(callerProps).filter(([name]) => isPropValid(name))
        )
      : { ...callerProps, inline, qa };

  return (
    <Component
      data-qa-chartlegend=""
      {...rootProps}
      className={classes}
      style={componentStyle}
    >
      {legendLabels.map((label, index) => (
        <span
          className="seeds-chart-legend-label"
          key={`chart-legend-label-${index}`}
        >
          <span
            className="seeds-chart-legend-swatch"
            data-qa-swatch=""
            style={{
              backgroundColor: getChartLegendSwatchColor(
                paletteTheme,
                index,
                racineTheme.colors
              ),
            }}
          />
          <div
            className="seeds-chart-legend-text"
            data-qa-text={
              typeof label.name === "string" ? label.name : undefined
            }
          >
            {label.name}
          </div>
        </span>
      ))}
    </Component>
  );
};

export default ChartLegendTailwind;
